/**
 * Shared "open consultation PDF" helper for Chain Admin + Pharmacy Admin.
 *
 * Consultation PDFs are end-to-end encrypted (AES-256-GCM) client-side by
 * the pharmacist app before upload — the server never holds the chain key
 * and cannot decrypt them. `getConsultationPdfUrl` (see
 * artifacts/functions/src/handlers/consultationFiles.ts) therefore returns
 * the raw object bytes (base64) plus metadata instead of a signed URL; this
 * helper decrypts them here in the browser (which already holds the chain
 * key via CryptoContext), falls back to treating the file as a legacy
 * unencrypted PDF when it wasn't E2EE-uploaded, validates the result is
 * actually a PDF, and returns a blob URL ready to be rendered.
 *
 * Callers should render the result in-app (see ./ConsultationPdfButton.tsx)
 * rather than pre-opening a tab: the fetch+decrypt round trip is slow enough
 * that a pre-opened tab sits visibly blank, and opening one afterwards has
 * lost the click's user activation and gets popup-blocked.
 */

import { callFn } from "./callable";

interface ConsultationPdfCallableResult {
  ok: boolean;
  base64: string;
  contentType: string;
  isEncrypted: boolean;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// "%PDF-" magic bytes.
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d];

function looksLikePdf(bytes: Uint8Array): boolean {
  if (bytes.length < PDF_MAGIC.length) return false;
  return PDF_MAGIC.every((b, i) => bytes[i] === b);
}

/**
 * Fetch, decrypt, and validate a consultation's PDF report, returning a
 * `blob:` URL suitable for an `<iframe src>` (or an anchor the user clicks).
 *
 * Throws a user-facing `Error` (never a raw HttpsError) if the callable
 * fails, decryption fails, or the decrypted bytes are not a valid PDF.
 *
 * @param consultationId The consultation whose report should be opened.
 * @param decryptFileBytes `useCrypto().decryptFileBytes` — decrypts raw
 *   bytes with the chain key. Only invoked when the file is E2EE-encrypted.
 */
export async function openConsultationPdf(
  consultationId: string,
  decryptFileBytes: (data: Uint8Array) => Promise<Uint8Array>,
): Promise<string> {
  let result: ConsultationPdfCallableResult;
  try {
    result = await callFn<{ consultationId: string }, ConsultationPdfCallableResult>(
      "getConsultationPdfUrl",
      { consultationId },
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : "Could not fetch the PDF report.";
    throw new Error(message);
  }

  const rawBytes = base64ToUint8Array(result.base64);

  let pdfBytes: Uint8Array;
  if (result.isEncrypted) {
    try {
      pdfBytes = await decryptFileBytes(rawBytes);
    } catch (err) {
      console.error("[consultationPdf] decryption failed", err);
      throw new Error(
        "Could not decrypt the PDF report — the encryption key may not be loaded yet. Please try again in a moment.",
      );
    }
  } else {
    // Legacy pre-E2EE upload — stored as plaintext.
    pdfBytes = rawBytes;
  }

  if (!looksLikePdf(pdfBytes)) {
    throw new Error("The downloaded file is not a valid PDF report.");
  }

  const blob = new Blob([pdfBytes.slice().buffer], {
    type: result.contentType || "application/pdf",
  });
  return URL.createObjectURL(blob);
}
